/**
 * AuthContext - Kimlik doğrulama ve oturum yönetimi (Supabase Auth)
 *
 * Giriş Supabase Auth (signInWithPassword) ile yapılır — şifreler
 * Supabase tarafında bcrypt ile hash'lenir, client'a asla dönmez.
 * Profil/rol bilgisi agents tablosundan (RLS korumalı) okunur.
 * Yetkilendirme artık client'a değil, DB (RLS) + serverless katmanına dayanır;
 * buradaki role/permission alanları yalnızca UI görünürlüğü içindir.
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import { initSupabase } from './adapters/SupabaseAdapter';
import { resolvePermissions, canAccess as canAccessHelper } from '@/lib/permissions';

const AuthContext = createContext(null);

// Supabase client'ı al
function getSupabase() {
    const url = import.meta.env?.VITE_SUPABASE_URL;
    const key = import.meta.env?.VITE_SUPABASE_ANON_KEY;
    if (!url || !key) return null;
    return initSupabase(url, key);
}

/** Auth hata mesajlarını kullanıcı diline çevirir */
function translateAuthError(message) {
    const msg = (message || '').toLowerCase();
    if (msg.includes('invalid login credentials')) return 'E-Mail oder Passwort falsch.';
    if (msg.includes('email not confirmed')) return 'E-Mail-Adresse nicht bestätigt.';
    if (msg.includes('too many requests') || msg.includes('rate limit')) {
        return 'Zu viele Versuche. Bitte kurz warten.';
    }
    return message || 'Anmeldung fehlgeschlagen.';
}

/** agents satırından UI user objesi üretir (passwort ASLA seçilmez) */
const PROFILE_COLUMNS =
    'id, email, name, vorname, nachname, rolle, gebiet, telefon, position, avatar_url, screen_permissions, aktiv, auth_user_id';

function toUserData(agent) {
    return {
        id: agent.id,
        email: agent.email,
        name: agent.name,
        vorname: agent.vorname,
        nachname: agent.nachname,
        role: (agent.rolle || agent.role || '').toLowerCase(),
        gebiet: agent.gebiet,
        telefon: agent.telefon,
        position: agent.position,
        avatar_url: agent.avatar_url,
        screen_permissions: agent.screen_permissions || {},
        auth_user_id: agent.auth_user_id,
    };
}

/** Rolle aus dem Access-Token lesen (nur UI-Weiche; geprüft wird in DB/Server) */
function tokenRole(accessToken) {
    try {
        const payload = accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(atob(payload.padEnd(Math.ceil(payload.length / 4) * 4, '='))).role || null;
    } catch {
        return null;
    }
}

/** Portal-Kunde: Profil kommt aus portal_me (nur eigene Daten) */
function toPortalUser(authUser, me) {
    return {
        id: authUser.id,
        email: me?.email || authUser.email,
        name: me?.kunde?.name || authUser.email,
        role: 'kunde',
        screen_permissions: {},
        auth_user_id: authUser.id,
        portal: me,
    };
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const userRef = useRef(null);
    userRef.current = user;

    const profileErrorRef = useRef(null);

    /** Aktif auth oturumu için profili yükler (Personal: agents, Kunde: portal_me) */
    const loadProfile = useCallback(async (session) => {
        const supabase = getSupabase();
        const authUserId = session?.user?.id;
        profileErrorRef.current = null;
        if (!supabase || !authUserId) return null;

        if (tokenRole(session.access_token) === 'kunde_portal') {
            const { data: me, error: meError } = await supabase.rpc('portal_me');
            if (meError || !me) {
                profileErrorRef.current = meError?.code === 'P0001'
                    ? meError.message
                    : 'Kundenzugang konnte nicht geladen werden.';
                return null;
            }
            return toPortalUser(session.user, me);
        }

        const { data: agent, error } = await supabase
            .from('agents')
            .select(PROFILE_COLUMNS)
            .eq('auth_user_id', authUserId)
            .maybeSingle();

        if (error || !agent) return null;
        if (agent.aktiv === false) {
            await supabase.auth.signOut();
            return null;
        }
        return toUserData(agent);
    }, []);

    // Oturum başlatma + auth state takibi
    useEffect(() => {
        // Eski (güvensiz) oturum kalıntısını temizle
        sessionStorage.removeItem('inmotion_user');

        const supabase = getSupabase();
        if (!supabase) {
            setLoading(false);
            return;
        }

        let cancelled = false;

        // EMNİYET: oturum geri yükleme herhangi bir nedenle asılı kalırsa
        // kullanıcıyı sonsuz spinner'da bırakma — 10 sn sonra login'e düş.
        const failsafe = setTimeout(() => {
            if (!cancelled) setLoading(false);
        }, 10_000);

        (async () => {
            try {
                const { data: { session } } = await supabase.auth.getSession();
                if (cancelled) return;
                if (session?.user) {
                    const profile = await loadProfile(session);
                    if (!cancelled) setUser(profile);
                }
            } catch (err) {
                console.warn('[Auth] Session restore failed:', err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        // DİKKAT: onAuthStateChange callback'i Supabase auth KİLİDİ altında
        // çalışır. İçinde `await supabase.from(...)` yapmak (loadProfile)
        // kilidi tıkar ve sayfa yenilemede sonsuz beyaz ekran/deadlock
        // yaratır (supabase-js v2 bilinen davranışı). Bu yüzden callback
        // SENKRON kalır; profil yükleme setTimeout(0) ile kilidin DIŞINA
        // ertelenir.
        const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
            if (event === 'SIGNED_OUT') {
                setUser(null);
                return;
            }
            if (session?.user && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
                // Profil zaten yüklüyse (login() yolu) tekrar sorgulamaya gerek yok
                if (userRef.current?.auth_user_id === session.user.id) return;
                setTimeout(async () => {
                    if (cancelled) return;
                    try {
                        const profile = await loadProfile(session);
                        if (!cancelled && profile) setUser(profile);
                    } catch (err) {
                        console.warn('[Auth] Profile load failed:', err);
                    }
                }, 0);
            }
        });

        return () => {
            cancelled = true;
            clearTimeout(failsafe);
            listener?.subscription?.unsubscribe();
        };
    }, [loadProfile]);

    /**
     * E-posta ve şifre ile giriş (Supabase Auth). Rol DB'den tespit edilir.
     * Geriye dönük uyumluluk için `requiredRole` hâlâ kabul edilir.
     * @param {string} email
     * @param {string} password
     * @param {string|null} [requiredRole]
     * @returns {Promise<{success: boolean, error?: string, user?: object}>}
     */
    const login = async (email, password, requiredRole = null) => {
        try {
            const supabase = getSupabase();
            if (!supabase) {
                return { success: false, error: 'Supabase bağlantısı kurulamadı.' };
            }

            const { data, error: signInError } = await supabase.auth.signInWithPassword({
                email: email.trim().toLowerCase(),
                password,
            });

            if (signInError) {
                return { success: false, error: translateAuthError(signInError.message) };
            }

            const profile = await loadProfile(data.session);
            if (!profile) {
                const reason = profileErrorRef.current;
                await supabase.auth.signOut();
                return { success: false, error: reason || 'Konto nicht gefunden oder deaktiviert. Bitte Admin kontaktieren.' };
            }

            // Geriye dönük rol kontrolü — admin her alana girebilir
            if (requiredRole) {
                const required = requiredRole.toLowerCase();
                if (profile.role !== 'admin' && profile.role !== required) {
                    await supabase.auth.signOut();
                    const labels = { admin: 'Admin', agent: 'Agent', warehouse: 'Lager', shipping: 'Versand' };
                    return { success: false, error: `Sie haben keine ${labels[required] || required}-Berechtigung.` };
                }
            }

            setUser(profile);
            return { success: true, user: profile };
        } catch (err) {
            return { success: false, error: 'Verbindungsfehler: ' + err.message };
        }
    };

    /** Çıkış yapar */
    const logout = async () => {
        const supabase = getSupabase();
        if (supabase) await supabase.auth.signOut();
        setUser(null);
        sessionStorage.removeItem('inmotion_user');
    };

    /**
     * Mevcut oturumdaki kullanıcının parolasını doğrular (hassas işlemler için).
     * Aktif oturumu ETKİLEMEZ — geçici, kalıcı olmayan bir client kullanır.
     * @param {string} plainPassword
     * @returns {Promise<{success: boolean, error?: string}>}
     */
    const verifyCurrentPassword = async (plainPassword) => {
        if (!user?.email) {
            return { success: false, error: 'Keine aktive Sitzung.' };
        }
        if (!plainPassword) {
            return { success: false, error: 'Bitte Passwort eingeben.' };
        }
        try {
            const url = import.meta.env?.VITE_SUPABASE_URL;
            const key = import.meta.env?.VITE_SUPABASE_ANON_KEY;
            if (!url || !key) {
                return { success: false, error: 'Supabase bağlantısı kurulamadı.' };
            }
            // Ana oturumu bozmamak için ayrı, persist etmeyen client
            const tempClient = createClient(url, key, {
                auth: { persistSession: false, autoRefreshToken: false },
            });
            const { error } = await tempClient.auth.signInWithPassword({
                email: user.email.trim().toLowerCase(),
                password: plainPassword,
            });
            if (error) {
                return { success: false, error: 'Falsches Passwort.' };
            }
            await tempClient.auth.signOut();
            return { success: true };
        } catch (err) {
            return { success: false, error: 'Verbindungsfehler: ' + err.message };
        }
    };

    /** Kullanıcının belirli bir role sahip olup olmadığını kontrol eder */
    const hasRole = (role) => {
        if (!user) return false;
        return user.role === role.toLowerCase();
    };

    // Resolved permission map — re-computed when user changes
    const permissions = useMemo(() => resolvePermissions(user), [user]);
    const canAccess = (screenName) => canAccessHelper(user, screenName);

    const value = {
        user,
        loading,
        login,
        logout,
        hasRole,
        verifyCurrentPassword,
        permissions,
        canAccess,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        isAgent: user?.role === 'agent',
        isWarehouse: user?.role === 'warehouse',
        isShipping: user?.role === 'shipping',
        isKunde: user?.role === 'kunde',
        /** Portal-Profil neu laden (z. B. nach Einstellungsänderung) */
        reloadProfile: async () => {
            const supabase = getSupabase();
            const { data: { session } } = await supabase.auth.getSession();
            const profile = await loadProfile(session);
            if (profile) setUser(profile);
            return profile;
        },
        canAccessAnyRoleView: user?.role === 'admin',
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

/**
 * Wie useAuth, aber ohne Provider-Pflicht (null statt Fehler) — für
 * Komponenten, die auch außerhalb des AuthProviders (Tests) rendern.
 */
export function useOptionalAuth() {
    return useContext(AuthContext);
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return ctx;
}

export default AuthContext;
